<script setup lang="ts">
import { computed, inject, ref, watch } from 'vue'
import { RouterLink, useRoute } from 'vue-router'
import NotificationItem from './NotificationItem.vue'
import type { SimpleNotification } from '@/composables/useNotifications/index'
import type { RouteLocationRaw } from 'vue-router'

const emit = defineEmits<{
  'item-click': [item: SimpleNotification, index: number]
}>()

const props = defineProps<{
  notifications: SimpleNotification[]
  noMaxHeight?: boolean
  selectable?: boolean
  itemTo?: string | RouteLocationRaw | ((item: SimpleNotification) => RouteLocationRaw) | null
  loading?: boolean
  loadingCount?: number
}>()

const isLinked = computed(() => props.itemTo !== null && props.itemTo !== undefined)

const resolveTo = (item: SimpleNotification): RouteLocationRaw => {
  const itemTo = props.itemTo as
    | RouteLocationRaw
    | ((item: SimpleNotification) => RouteLocationRaw)
    | string

  return typeof itemTo === 'function' ? itemTo(item) : (itemTo as RouteLocationRaw)
}

const handleItemClick = (item: SimpleNotification, index: number) => {
  emit('item-click', item, index)
}

// Route-driven focus selection
const route = useRoute()
const selectNotification = inject<(notification: SimpleNotification) => void>(
  'selectNotification',
  () => {},
)
const lastFocusedId = ref<string | null>(null)

const selectFocused = () => {
  const raw = route.query?.focus
  const focusId = Array.isArray(raw) ? raw[0] : raw
  if (!focusId || typeof focusId !== 'string') return
  if (lastFocusedId.value === focusId) return

  const match = props.notifications.find((n) => n.id === focusId)
  if (match) {
    lastFocusedId.value = focusId
    selectNotification(match)
  }
}

watch(
  () => route.query.focus,
  () => selectFocused(),
)

watch(
  () => props.notifications,
  () => selectFocused(),
  { immediate: true },
)
</script>

<template>
  <div v-if="loading" class="py-2" aria-busy="true">
    <slot name="loading">
      <ol
        aria-label="Loading notifications"
        class="flex flex-col divide-y divide-gray-200 dark:divide-gray-800"
        :class="{ 'overflow-y-scroll max-h-96': !noMaxHeight, 'h-full': noMaxHeight }"
      >
        <li v-for="i in loadingCount ?? 6" :key="i" class="isolate relative py-2">
          <div class="block relative px-1 py-1.5 w-full">
            <div class="space-y-2">
              <div class="w-3/4 h-4 bg-gray-200 rounded animate-pulse dark:bg-white/10" />
              <div class="w-1/4 h-3 bg-gray-200 rounded animate-pulse dark:bg-white/10" />
            </div>
          </div>
        </li>
      </ol>
    </slot>
  </div>
  <div
    v-else-if="notifications.length === 0"
    class="py-6 text-sm text-center text-gray-500 dark:text-gray-400"
  >
    <slot name="empty">No notifications</slot>
  </div>
  <ol
    v-else
    aria-label="Notifications"
    class="flex flex-col divide-y divide-gray-200 dark:divide-gray-800"
    :class="{ 'overflow-y-scroll max-h-96': !noMaxHeight, 'h-full': noMaxHeight }"
  >
    <li v-for="(notification, index) in notifications" :key="notification.id">
      <RouterLink
        v-if="isLinked"
        :to="resolveTo(notification)"
        class="block"
        @click="handleItemClick(notification, index)"
      >
        <NotificationItem
          :notification="notification"
          :selectable="selectable"
          :is-linked="isLinked"
        />
      </RouterLink>
      <div v-else class="block" @click="handleItemClick(notification, index)">
        <NotificationItem
          :notification="notification"
          :selectable="selectable"
          :is-linked="isLinked"
        />
      </div>
    </li>
  </ol>
</template>
